import {testIdProps} from '@github-ui/test-id-props'
import {TriangleDownIcon} from '@primer/octicons-react'
import type {SelectPanelItemInput as ItemInput} from '@primer/react'
import {Button, SelectPanel} from '@primer/react'
import {type ComponentProps, useCallback, useMemo, useState} from 'react'

import type {PersistedOption} from '../../api/columns/contracts/single-select'
import {WorkflowResources} from '../../strings'
import {SanitizedHtml} from '../dom/sanitized-html'
import {buildPickerLabel} from './helpers/accessible-label'

type StatusPickerProps = ComponentProps<'div'> & {
  onStatusSelected: (option: PersistedOption) => void
  options: Array<PersistedOption>
  selectedOption: PersistedOption | undefined
  isEditing?: boolean
  testId: string
  headerDescription?: string
}

export const StatusPicker: React.FC<StatusPickerProps> = ({
  selectedOption,
  options,
  isEditing,
  onStatusSelected,
  testId,
  headerDescription,
}) => {
  const items = useMemo(
    () =>
      options.map(
        (option: PersistedOption) =>
          ({
            text: option.name,
            id: option.name,
          }) as ItemInput,
      ),
    [options],
  )

  const [filter, setFilter] = useState('')
  const [isOpen, setIsOpen] = useState(false)

  const onClose = useCallback(() => {
    setIsOpen(false)
  }, [])

  const onSelectedChange = (selectedItem: ItemInput | undefined) => {
    onStatusSelected(options.find(option => option.name === selectedItem?.text) as PersistedOption)
  }

  const filteredItems = items.filter(item => item.text?.toLowerCase().startsWith(filter.toLowerCase()))
  const selected = items.find(item => item.text === selectedOption?.name)

  // Build the visible + accessible label:
  const label = buildPickerLabel(headerDescription, selectedOption?.name)
  return (
    <SelectPanel
      placeholderText={WorkflowResources.setValueLabel}
      open={isOpen}
      onOpenChange={setIsOpen}
      selected={selected}
      onFilterChange={setFilter}
      items={filteredItems}
      onSelectedChange={onSelectedChange}
      renderAnchor={({children, ...anchorProps}) => (
        <Button
          disabled={!isEditing}
          trailingVisual={TriangleDownIcon}
          {...anchorProps}
          {...testIdProps(`${testId}-anchor`)}
          aria-label={label}
        >
          {selectedOption ? (
            <SanitizedHtml>{`Status: ${selectedOption?.nameHtml}`}</SanitizedHtml>
          ) : (
            WorkflowResources.invalidNoValue
          )}
        </Button>
      )}
      overlayProps={{
        width: 'small',
        onMouseDown: e => e.stopPropagation(),
        height: 'auto',
        onClickOutside: onClose,
        ...testIdProps(`${testId}-panel`),
      }}
    />
  )
}
